import { formatHourlyRate, formatHours, formatNOK, formatPct } from "@/lib/format";
import type { BfRankEntry, TimeprisRankEntry } from "@/lib/top-projects-calc";
import type { SalesUnitHourlyRateRow } from "@/lib/sales-unit-hourly-rate";

const MEDALS = ["🥇", "🥈", "🥉"];

interface RankingRow {
  id: string;
  name: string;
  context: string;
  detail: string;
  value: string;
}

type DisplayHallOfFameSlideProps =
  | { variant: "timepris"; rows: TimeprisRankEntry[] }
  | { variant: "geek-under-budget"; rows: BfRankEntry[] }
  | { variant: "sales-unit-hourly-rate"; rows: SalesUnitHourlyRateRow[] };

function credits(row: TimeprisRankEntry | BfRankEntry): string {
  const sales = row.salesOwnerNames.join(", ") || "—";
  return `Salg: ${sales} · PL: ${row.projectManagerName ?? "—"}`;
}

function projectName(row: TimeprisRankEntry | BfRankEntry): string {
  return row.code ? `${row.code} · ${row.name}` : row.name;
}

function slideContent(props: DisplayHallOfFameSlideProps): {
  title: string;
  subtitle: string;
  rows: RankingRow[];
} {
  if (props.variant === "timepris") {
    return {
      title: "Beste timepris",
      subtitle: "Avsluttede prosjekter med over 100 solgte timer",
      rows: props.rows.slice(0, 3).map((row) => ({
        id: row.id,
        name: projectName(row),
        context: row.customerName || "Kunde ikke angitt",
        detail: `${credits(row)} · ${formatHours(row.soldHours)} solgt`,
        value: formatHourlyRate(row.timeprisKr),
      })),
    };
  }

  if (props.variant === "geek-under-budget") {
    return {
      title: "Beste GEEK under budsjett",
      subtitle: "Avsluttede prosjekter med høyest GEEK og færre brukte enn solgte timer",
      rows: props.rows.slice(0, 3).map((row) => ({
        id: row.id,
        name: projectName(row),
        context: row.customerName || "Kunde ikke angitt",
        detail: `${credits(row)} · ${formatPct(row.deviationPct)} under budsjett`,
        value: formatNOK(row.bfActual),
      })),
    };
  }

  return {
    title: "Reell timepris per selger",
    subtitle: "Reell GEEK / førte timer · fullførte prosjekter hittil i år",
    rows: props.rows
      .filter((row) => row.hourlyRate != null)
      .slice(0, 3)
      .map((row) => ({
        id: row.unitId,
        name: row.unitName,
        context: `${row.includedProjectCount} fullførte prosjekt`,
        detail: `${formatNOK(row.geekAmount)} GEEK · ${formatHours(row.loggedHours)} ført`,
        value: formatHourlyRate(row.hourlyRate),
      })),
  };
}

/** TV slide: one Måltall ranking per full-screen slide. */
export function DisplayHallOfFameSlide(props: DisplayHallOfFameSlideProps) {
  const { title, subtitle, rows } = slideContent(props);

  return (
    <div className="flex h-full flex-col justify-center px-12 py-8 2xl:px-20">
      <p className="text-lg font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Hall of Fame · Topp 3
      </p>
      <h2 className="mt-2 text-5xl font-bold tracking-tight 2xl:text-7xl">{title}</h2>
      <p className="mt-3 text-xl text-muted-foreground 2xl:text-2xl">{subtitle}</p>

      {rows.length === 0 ? (
        <p className="mt-16 text-2xl text-muted-foreground">Ingen kvalifiserte data ennå.</p>
      ) : (
        <ol className="mt-10 space-y-5 2xl:mt-14 2xl:space-y-7">
          {rows.map((row, index) => (
            <li
              key={row.id}
              className="grid grid-cols-[5rem_minmax(0,1fr)_auto] items-center gap-6 rounded-2xl border border-border/70 bg-card px-7 py-5 2xl:grid-cols-[6rem_minmax(0,1fr)_auto] 2xl:px-10 2xl:py-7"
            >
              <span className="text-center text-4xl 2xl:text-5xl" aria-label={`Plass ${index + 1}`}>
                {MEDALS[index]}
              </span>
              <div className="min-w-0">
                <p className="truncate text-2xl font-bold 2xl:text-4xl">{row.name}</p>
                <p className="mt-1 truncate text-lg text-muted-foreground 2xl:text-2xl">{row.context}</p>
                <p className="mt-1 truncate text-sm text-muted-foreground 2xl:text-lg">{row.detail}</p>
              </div>
              <span className="pl-6 text-right font-display text-3xl font-semibold tabular-nums text-primary 2xl:text-5xl">
                {row.value}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
